import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { faPresentationScreen } from "@fortawesome/pro-solid-svg-icons/faPresentationScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Card, CardContent, Grid, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcContainer, MprcInfiniteLoading, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from 'components';
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcSwitchPlayback } from "components/MprcSwitchPlayback";
import { FeedAttachDto, FeedContentLoadStatus, FeedItemDto, TrainingPublicationItemDto } from "generated/CoreApi_v1";
import { useWindowSize } from "hooks/useWindowSize";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { CardOwnPropsOptions } from "views/knowledge/components/card";
import { BuildLigthBoxSlides, CardLightBox } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { InternalContentMedia } from "views/knowledge/components/card/behaviors/common/ContentMedia";
import { InternalAttach } from "views/knowledge/components/card/behaviors/common/DefaultAttach";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";
import { PrepareCardOwnProps } from "views/knowledge/components/card/PrepareCardOwnProps";
import { PublicationContentBox } from "views/knowledge/publication/pages/components/PublicationContentBox";
import { PublicationHeader } from "views/lms/student/Publication/PublicationHeader";
import { RightPanelContentPage } from "views/lms/student/Publication/RightPanelContentPage";

////////////////////////////////////////////////////////////////////////////////////////////////

export const TrainingPostViewer: React.FC = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const { showUser } = useModalUserProfile();

    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("lms");
    const { width } = useWindowSize({ wait: 50 });

    const [loading, setLoading] = useState<boolean>(true);
    const [localContent, setLocalContent] = useState<string>("");
    const [publication, setPublication] = useState<TrainingPublicationItemDto>();
    const [cardOwnProps, setCardOwnProps] = useState<CardOwnPropsOptions>();
    const [viewError, setViewError] = useState<any>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [loadingContent, setLoadingContent] = useState<boolean>(false);
    const feedItemHelper = useRef<FeedItemHelper | undefined>(undefined);
    const currentMedia = useRef<FeedAttachDto | null>(null);
    const firstPlay = useRef<boolean>(true);

    ///
    const slides = useMemo(() => (
        publication && publication.presentation && publication.attachs
            ? BuildLigthBoxSlides(publication.presentation, publication.attachs, publication.presentation.rotationInterval == 0)
            : []
    ), [publication]);

    ///
    const load = (publicationId: string) => {
        setLoading(true);
        api.v1.lmsPublication.getTrainingContentPostItem({ publicationId })
            .then((result) => {
                feedItemHelper.current = new FeedItemHelper(result.data as unknown as FeedItemDto);
                setCardOwnProps(PrepareCardOwnProps(result.data as unknown as FeedItemDto, feedItemHelper.current));
                setPublication(result.data);
                setLoading(false);
            })
            .catch((error: any) => {
                setViewError(error);
                setLoading(false);
            });
    };

    /////////////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    useEffect(() => {
        if (id) load(id);
    }, [location.state]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// MEDIA
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        currentMedia.current = publication?.attachs?.find(p => p.attachId == cardOwnProps?.presentation.currentAttachId) ?? null;
        firstPlay.current = true;
    }, [cardOwnProps]);

    ///
    const onMediaPlay = (id: string, seconds: number) => {
        const m = currentMedia.current;
        if (m == null || m.mediaCompleted == true) {
            // Não precisa verificar SEEK.
            return null;
        }

        if (!firstPlay.current) {
            // Não é a primeira vez que pressionou PLAY, então verificar se pode fazer SEEK.
            return Math.min(seconds, m.lastMediaPosition ?? 0);
        }

        // Ao pressionar PLAY a primeira vez, posiciona a mídia na última posição registrada.
        firstPlay.current = false;
        return m.lastMediaPosition ?? 0;
    };

    ///
    const onConfirmMediaSeek = (id: string, seekSeconds: number) => {
        const m = currentMedia.current;
        return m == null || m.mediaCompleted == true
            ? { playTime: seekSeconds, threshold: 0 }
            : m.mediaCompleted == false
                ? { playTime: Math.min(seekSeconds, m.lastMediaPosition ?? 0), threshold: 0 }
                : { playTime: m.lastMediaPosition ?? 0, threshold: 0 };
    };

    ///
    const loadContent = (publicationId: string) => {
        setLoadingContent(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                setLocalContent(result.data);
                setExpanded(true);
                setPublication(state => ({ ...state!, contentLoadStatus: FeedContentLoadStatus.AllContentLoaded }));
            })
            .catch((e) => console.error(e))
            .finally(() => setLoadingContent(false));
    };

    ///
    const toogleStatus = () => {
        if (publication!.contentLoadStatus === FeedContentLoadStatus.AllContentLoaded) {
            setExpanded(!expanded);
        }
        else if (publication!.contentLoadStatus === FeedContentLoadStatus.LoadContentInDemand) {
            loadContent(publication!.publicationId);
        }
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderAutor = useCallback(() => {

        if (publication == undefined) return null;

        const pub = publication;

        const actionName = pub.contentModifiedDate && pub.contentModifiedDate?.getTime() != pub.createdOn.getTime()
            ? t("Feed.Card.Common.ModifiedOnText", { ns: "knowledge" })
            : t("Feed.Card.Common.PublishOnText", { ns: "knowledge" });

        const actionDate = pub.contentModifiedDate && pub.contentModifiedDate != pub.createdOn
            ? pub.contentModifiedDate
            : pub.createdOn;

        return (
            <>
                <Grid container justifyContent="center" alignItems="center" spacing={1}>
                    <Grid>
                        <Avatar
                            className={AppGlobalClassKeys.clickable}
                            src={pub.author.thumbnailUrl}
                            onClick={
                                () => showUser(
                                    pub.author.userId,
                                    () => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: pub.publicationId } })
                                )
                            }
                        />
                    </Grid>
                    <Grid size="grow">
                        <MprcLink
                            onClick={
                                () => showUser(
                                    pub.author.userId,
                                    () => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: pub.publicationId } })
                                )
                            }
                        >
                            <MprcFeedTypography variant="authorPrimary" component="div">
                                <span title={pub.author.name}>{pub.author.name}</span>
                            </MprcFeedTypography>
                        </MprcLink>

                        <MprcFeedTypography variant="authorSecondary" component="div">
                            <span>{actionName} {Formaters.relativeTime(actionDate)}</span>
                        </MprcFeedTypography>
                    </Grid>
                </Grid>
            </>
        );
    }, [publication]);

    const RenderPublication = useCallback(() => (
        <>
            {loading
                ? <Skeleton variant="rectangular" width="100%" height={"500px"} />
                :
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    : publication &&
                    <MprcContainer>
                        <Card>
                            <CardContent sx={{ p: 1, pb: 0 }}>
                                <Grid container width="100%" direction="column" gap={1}>
                                    <Grid id="mediaHeaderSection" width="100%">
                                        <InternalContentMedia
                                            publicationId={publication.publicationId}
                                            presentation={publication.presentation}
                                            attachs={publication.attachs}
                                            cardOwnProps={cardOwnProps!}
                                            onUpdateCardOwnProps={(data: Partial<CardOwnPropsOptions>) =>
                                                setCardOwnProps((state) => {
                                                    const newState = { ...state!, ...data };
                                                    return newState;
                                                })
                                            }
                                            videoEvents={
                                                {
                                                    onPlay: onMediaPlay,
                                                    onConfirmSeek: onConfirmMediaSeek
                                                }
                                            }
                                        />
                                        {
                                            feedItemHelper.current != undefined &&
                                            (feedItemHelper.current.hasAutoPlayPresentation || feedItemHelper.current.hasPresentation) &&
                                            <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                                                {
                                                    feedItemHelper.current.hasAutoPlayPresentation &&
                                                    <Grid>
                                                        <MprcSwitchPlayback
                                                            checked={cardOwnProps!.presentation.autoplay}
                                                            onChange={
                                                                (_, checked) => setCardOwnProps({ ...cardOwnProps!, presentation: { ...cardOwnProps!.presentation, autoplay: checked } })
                                                            }
                                                        />
                                                    </Grid>
                                                }
                                                {
                                                    feedItemHelper.current.hasPresentation && publication.presentation &&
                                                    <Grid>
                                                        <Typography variant="iconText">
                                                            <IconButton color="inherit"
                                                                onClick={() => {
                                                                    setOpenLightbox(true);
                                                                    setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: true } }));
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faPresentationScreen} color="inherit" />
                                                            </IconButton>
                                                        </Typography>
                                                        <CardLightBox
                                                            slides={slides}
                                                            open={openLightbox}
                                                            index={0}
                                                            autoplay={publication.presentation.rotationInterval > 0}
                                                            rotationInterval={publication.presentation.rotationInterval > 0 ? publication.presentation!.rotationInterval : 5}
                                                            onClose={() => {
                                                                setOpenLightbox(false);
                                                                setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: false } }));
                                                            }}
                                                        />
                                                    </Grid>
                                                }
                                            </Grid>
                                        }
                                    </Grid>
                                    <Grid width="100%">
                                        <Grid container gap={1}>
                                            <Grid size="grow" container direction="column" gap={1} width={"100%"}>
                                                <Grid id="titleSection" width="100%">
                                                    <MprcFeedTypography variant="title" component="div" noWrap={true} color="black">
                                                        <span title={publication.title}>{publication.title}</span>
                                                    </MprcFeedTypography>
                                                </Grid>
                                                <Grid id="author" width="100%">
                                                    {RenderAutor()}
                                                </Grid>
                                                <Grid width="100%">
                                                    <Grid container gap={1} justifyContent="center">
                                                        <Grid id="contentSection" className={AppGlobalClassKeys.qlContentSection} width="100%">
                                                            <MprcFeedTypography variant="content" component="div">
                                                                <div dangerouslySetInnerHTML={{
                                                                    __html: !expanded || publication.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary
                                                                        ? publication.content ?? ""
                                                                        : localContent
                                                                }}></div>
                                                            </MprcFeedTypography>
                                                            {
                                                                publication.contentLoadStatus !== FeedContentLoadStatus.NoActionNecessary &&
                                                                <>
                                                                    {
                                                                        loadingContent
                                                                            ? <MprcSpinnerIndicator />
                                                                            : <MprcFeedTypography variant="moreContent" component="a" onClick={() => toogleStatus()}>
                                                                                {
                                                                                    expanded
                                                                                        ? t("knowledge:Feed.Card.Common.SeeLessText")
                                                                                        : t("knowledge:Feed.Card.Common.SeeMoreText")
                                                                                }
                                                                            </MprcFeedTypography>
                                                                    }
                                                                </>
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid id="attachSection" width="100%">
                                        <InternalAttach
                                            publicationId={publication.publicationId}
                                            attachs={publication.attachs}
                                            helper={feedItemHelper.current!}
                                        />
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </MprcContainer>
            }
        </>
    ), [publication, expanded]);

    ///
    const headerActions = useMemo(() => {
        var headerItems: React.ReactNode[] = [];

        if (width < theme.breakpoints.values["md"])
            headerItems.push([<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]);

        return headerItems;

    }, [publication, width]);

    return (
        <>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : publication != undefined &&
                        <>
                            <MprcContainer position="sticky" margin="none">
                                <PublicationHeader
                                    onClose={gobackOrHome}
                                    actions={headerActions}
                                />
                            </MprcContainer>

                            <PublicationContentBox
                                openRightPanel={openRightPanel}
                                onCloseRighPanel={() => setOpenRightPanel(false)}
                                maxWidth="xl"
                                content={RenderPublication()}
                                rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                            />
                        </>
            }
        </>
    );
};
